import { Button } from "@legal-500/legal500-design-system";
import { downloadReportCsv } from "./testimonialStatusCsv.js";
import type {
  TestimonialStatusBatchProgress,
  TestimonialStatusBatchTotals,
} from "./useTestimonialStatusBatch.js";
import styles from "../PipelineBrowser.module.css";

type CounterRow = { label: string; key: keyof TestimonialStatusBatchTotals };

const COUNTERS: CounterRow[] = [
  { label: "Rows received", key: "received" },
  { label: "Not found", key: "notFound" },
  { label: "Unchanged (already in target status)", key: "unchanged" },
  { label: "Skipped (approval request WITHDRAWN)", key: "withdrawnSkipped" },
  { label: "Not suspendable (not APPROVED)", key: "notSuspendable" },
  { label: "Testimonials updated", key: "updatedTestimonials" },
  { label: "Testimonial-only (copies / translations)", key: "testimonialOnly" },
  { label: "Processing items updated", key: "updatedProcessingItems" },
  { label: "Approval requests updated", key: "updatedApprovalRequests" },
];

type TestimonialStatusRunReportProps = {
  totals: TestimonialStatusBatchTotals;
  progress: TestimonialStatusBatchProgress;
  dryRun: boolean;
  stopped: boolean;
  operationAt: string | null;
};

export function TestimonialStatusRunReport({
  totals,
  progress,
  dryRun,
  stopped,
  operationAt,
}: TestimonialStatusRunReportProps) {
  const reportRows =
    totals.notFoundIds.length + totals.withdrawnSkippedIds.length + totals.notSuspendableIds.length;

  return (
    <div className={styles.syncStatePanel}>
      <div className={styles.contentSectionTitle}>
        Result — {dryRun ? "dry run (nothing written)" : "applied"}
        {stopped ? " — stopped by user" : ""}
      </div>
      <div className={styles.syncStateMessage}>
        Processed {progress.processedRows} / {progress.totalRows} rows in {progress.batch} /{" "}
        {progress.totalBatches} batches.
        {stopped && progress.processedRows < progress.totalRows
          ? ` Rows ${progress.processedRows + 1}–${progress.totalRows} were not sent.`
          : ""}
      </div>
      <table className={styles.reportTable}>
        <tbody>
          {COUNTERS.map(counter => (
            <tr key={counter.key}>
              <td>{counter.label}</td>
              <td>{totals[counter.key] as number}</td>
            </tr>
          ))}
          <tr>
            <td>operationAt</td>
            <td>
              <code>{operationAt ?? "-"}</code>
              <div className={styles.mappingHint}>
                updated_at value shared by all three tables for this operation
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      <div className={styles.syncStateRow}>
        <Button
          variant="outlined"
          color="secondary"
          size="small"
          disabled={reportRows === 0}
          onClick={() => downloadReportCsv(totals, dryRun, new Date())}
        >
          Download report CSV ({reportRows} rows)
        </Button>
      </div>
    </div>
  );
}
